import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI, projectAPI, leadAPI, micrositeAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Briefcase, Globe, DollarSign, TrendingUp, Plus, ArrowUpRight, Building2, Eye, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className="summary-card">
    <CardContent className="p-4">
      <div className={`p-2 rounded-lg ${color} w-fit`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
      <p className="text-2xl font-bold mt-3">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{title}</p>
    </CardContent>
  </Card>
);

const LeadItem = ({ lead, getStatusColor }) => (
  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
    <div>
      <p className="font-medium">{lead.name}</p>
      <p className="text-sm text-muted-foreground">{lead.phone}</p>
    </div>
    <Badge className={getStatusColor(lead.status)}>
      {lead.status?.replace(/_/g, ' ')}
    </Badge>
  </div>
);

const MicrositeItem = ({ site }) => (
  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
    <div>
      <p className="font-medium">{site.title}</p>
      <p className="text-sm text-muted-foreground">/{site.slug}</p>
    </div>
    <div className="flex items-center gap-2">
      <Badge variant={site.status === 'published' ? 'default' : 'secondary'}>
        {site.status}
      </Badge>
      <span className="flex items-center text-sm text-muted-foreground">
        <Eye className="h-3 w-3 mr-1" />
        {site.analytics?.views || 0}
      </span>
    </div>
  </div>
);

const ProjectCard = ({ project, onClick }) => (
  <div 
    className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
    onClick={onClick}
  >
    <Building2 className="h-8 w-8 text-amber-600 mb-3" />
    <p className="font-semibold">{project.name}</p>
    <p className="text-sm text-muted-foreground">{project.location || project.city}</p>
  </div>
);

const PartnerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [microsites, setMicrosites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [statsRes, projectsRes, leadsRes, micrositesRes] = await Promise.all([
          analyticsAPI.getDashboard(),
          projectAPI.list(),
          leadAPI.list({}),
          micrositeAPI.list(),
        ]);
        
        setStats(statsRes.data);
        setProjects(projectsRes.data.slice(0, 4));
        setRecentLeads(leadsRes.data.slice(0, 5));
        setMicrosites(micrositesRes.data.slice(0, 3));
      } catch (error) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, []);

  const getLeadStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-700',
      'contacted': 'bg-yellow-100 text-yellow-700',
      'qualified': 'bg-emerald-100 text-emerald-700',
      'site_visit_scheduled': 'bg-violet-100 text-violet-700',
      'booked': 'bg-green-100 text-green-700',
      'lost': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Welcome, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">Your partner dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/partner/leads')} variant="outline" data-testid="btn-add-lead">
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
          <Button onClick={() => navigate('/partner/microsites')} data-testid="btn-create-microsite">
            <Globe className="h-4 w-4 mr-2" />
            Create Microsite
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard title="Total Leads" value={stats?.total_leads || 0} icon={Briefcase} color="bg-blue-500" />
        <StatCard title="New Leads" value={stats?.new_leads || 0} icon={TrendingUp} color="bg-emerald-500" />
        <StatCard title="Site Visits" value={stats?.site_visits || 0} icon={Building2} color="bg-violet-500" />
        <StatCard title="Bookings" value={stats?.bookings || 0} icon={Users} color="bg-amber-500" />
        <StatCard title="Pending Commission" value={stats?.pending_commissions || 0} icon={DollarSign} color="bg-rose-500" />
        <StatCard title="Microsites" value={stats?.microsites || 0} icon={Globe} color="bg-cyan-500" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Recent Leads</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/partner/leads')}>
              View All <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No leads yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/partner/leads')}>
                  Add your first lead
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <LeadItem key={lead.id} lead={lead} getStatusColor={getLeadStatusColor} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">My Microsites</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/partner/microsites')}>
              View All <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {microsites.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No microsites created</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/partner/microsites')}>
                  Create your first microsite
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {microsites.map((site) => (
                  <MicrositeItem key={site.id} site={site} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Available Projects</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/partner/projects')}>
            View All <ArrowUpRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No projects assigned yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {projects.map((project) => (
                <ProjectCard 
                  key={project.id} 
                  project={project} 
                  onClick={() => navigate(`/partner/projects/${project.id}`)} 
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerDashboard;
